import * as THREE from "three";
import type { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { isMobileAgent, type QualityApplied, type QualitySettings } from "@rpg/look";
import { r2 } from "./stats";

// The report blocks every perf mode shares: the device, the quality tier, and the GPU texture memory estimate.

/** The GL renderer and vendor strings (unmasked when the extension allows). */
export function glStrings(gl: WebGL2RenderingContext): { renderer: string; vendor: string } {
  const ext = gl.getExtension("WEBGL_debug_renderer_info");
  return {
    renderer: String(ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER)),
    vendor: String(ext ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR)),
  };
}

/** Software GL (SwiftShader, llvmpipe and the like): frame times say nothing about a real GPU. */
export const isSoftwareGL = (glRenderer: string): boolean => /swiftshader|llvmpipe|software|softpipe|mesa offscreen/i.test(glRenderer);

/** The report's device block. */
export function deviceBlock(renderer: THREE.WebGLRenderer, gpuTimer: boolean): Record<string, unknown> {
  const gl = renderer.getContext() as WebGL2RenderingContext;
  const glS = glStrings(gl);
  const buf = renderer.getDrawingBufferSize(new THREE.Vector2());
  return {
    glRenderer: glS.renderer,
    glVendor: glS.vendor,
    softwareGL: isSoftwareGL(glS.renderer),
    userAgent: navigator.userAgent,
    mobileAgent: isMobileAgent(),
    devicePixelRatio: devicePixelRatio,
    pixelRatio: renderer.getPixelRatio(),
    cssSize: [innerWidth, innerHeight],
    drawingBuffer: [buf.x, buf.y],
    msaa: !!gl.getContextAttributes()?.antialias,
    gpuTimer,
    cores: navigator.hardwareConcurrency ?? null,
    deviceMemoryGB: (navigator as unknown as { deviceMemory?: number }).deviceMemory ?? null,
  };
}

/** The report's quality block: the tier's settings (infinite distances as "none") and what applying it did. */
export const qualityBlock = (q: Readonly<QualitySettings>, applied: QualityApplied): Record<string, unknown> => ({
  ...q,
  outlineFar: Number.isFinite(q.outlineFar) ? q.outlineFar : "none",
  nearCasterDist: Number.isFinite(q.nearCasterDist) ? q.nearCasterDist : "none",
  applied: { ...applied },
});

/** Bytes per texel of a texture or target's colour format (an estimate: RGBA unless it says otherwise). */
export function bytesPerTexel(t: THREE.Texture): number {
  const per = t.type === THREE.FloatType ? 4 : t.type === THREE.HalfFloatType ? 2 : t.type === THREE.UnsignedShortType || t.type === THREE.UnsignedInt248Type ? 2 : t.type === THREE.UnsignedIntType ? 4 : 1;
  const ch = t.format === THREE.RedFormat || t.format === THREE.DepthFormat || t.format === THREE.DepthStencilFormat ? 1 : t.format === THREE.RGFormat ? 2 : 4;
  return per * ch;
}

/** GPU texture memory, estimated from sizes: every texture on a scene material, every render target the shadow maps
 *  and post passes hold (only those three has actually uploaded or allocated), and the canvas (with MSAA). */
export function textureMemory(d: { renderer: THREE.WebGLRenderer; scene: THREE.Scene; composer: EffectComposer | null }): { totalMB: number; sceneMB: number; targetsMB: number; canvasMB: number; textures: number } {
  const props = (d.renderer as unknown as { properties: { get(o: object): Record<string, unknown> } }).properties;
  const onGpu = (t: THREE.Texture): boolean => props.get(t).__webglTexture !== undefined;
  const seen = new Set<string>();
  let scene = 0;
  const mip = (t: THREE.Texture): number => (t.generateMipmaps && t.minFilter !== THREE.LinearFilter && t.minFilter !== THREE.NearestFilter ? 4 / 3 : 1);
  const add = (v: unknown): void => {
    const t = v as THREE.Texture;
    if (!t || !t.isTexture || seen.has(t.uuid) || !onGpu(t)) return;
    seen.add(t.uuid);
    const img = t.image as { width?: number; height?: number } | undefined;
    if (img?.width && img.height) scene += img.width * img.height * bytesPerTexel(t) * mip(t);
  };
  d.scene.traverse((o) => {
    const mats = (o as THREE.Mesh).material;
    if (!mats) return;
    for (const m of Array.isArray(mats) ? mats : [mats]) {
      Object.values(m as unknown as Record<string, unknown>).forEach(add);
      for (const u of Object.values(((m as THREE.ShaderMaterial).uniforms ?? {}) as Record<string, { value: unknown } | undefined>)) add(u?.value);
    }
  });
  let targets = 0;
  const rt = (x: unknown): void => {
    const t = x as THREE.WebGLRenderTarget;
    if (!t || !t.isRenderTarget || !onGpu(t.texture)) return;
    targets += t.width * t.height * (bytesPerTexel(t.texture) + (t.depthBuffer ? 4 : 0)) * Math.max(1, t.samples);
  };
  d.scene.traverse((o) => {
    const l = o as THREE.DirectionalLight;
    if (l.isLight && l.shadow?.map) rt(l.shadow.map);
  });
  if (d.composer) {
    rt(d.composer.renderTarget1);
    rt(d.composer.renderTarget2);
    for (const p of d.composer.passes) for (const v of Object.values(p)) (Array.isArray(v) ? v : [v]).forEach(rt);
  }
  const buf = d.renderer.getDrawingBufferSize(new THREE.Vector2());
  const aa = d.renderer.getContext().getContextAttributes()?.antialias ? 4 : 1;
  const canvas = buf.x * buf.y * (4 + 4) * aa + buf.x * buf.y * 4; // colour + depth, multisampled, plus the resolve
  const MB = 1024 * 1024;
  return { totalMB: r2((scene + targets + canvas) / MB), sceneMB: r2(scene / MB), targetsMB: r2(targets / MB), canvasMB: r2(canvas / MB), textures: seen.size };
}
